import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './TagsTree.module.scss';

const UK_LOCALES = new Set(['ua-UK', 'ru-RU', 'ru', 'uk']);

export const isUkLocale = () => {
  const lng = typeof window !== 'undefined' ? localStorage.getItem('i18nextLng') : null;
  return UK_LOCALES.has(lng);
};

const getLevelEntry = (tag) => {
  if (!tag) return null;
  const depth = Math.max(1, tag.levels || 1);
  const key = `level_${depth - 1}`;
  return tag[key] || null;
};

export const getTagLeafName = (tag) => {
  const entry = getLevelEntry(tag);
  if (!entry) return '';
  return isUkLocale() ? entry.name_uk || entry.name_en || '' : entry.name_en || entry.name_uk || '';
};

export const getTagLevel2Name = (tag) => {
  if (!tag) return '';
  const entry = tag.level_1 || getLevelEntry(tag);
  return isUkLocale() ? entry.name_uk || entry.name_en || '' : entry.name_en || entry.name_uk || '';
};

export const getTagLevel2PathLabel = (tag, separator = ' › ') => {
  if (!tag) return '';
  const parts = [];
  for (let i = 0; i < 2; i += 1) {
    const entry = tag[`level_${i}`];
    if (!entry) break;
    parts.push(isUkLocale() ? entry.name_uk || entry.name_en : entry.name_en || entry.name_uk);
  }
  return parts.join(separator);
};

export const getTagFullPathLabel = (tag, separator = ' › ') => {
  if (!tag) return '';
  const arr = isUkLocale() ? tag.name_uk_full : tag.name_en_full;
  if (Array.isArray(arr) && arr.length > 0) {
    return arr.join(separator);
  }
  // Fallback: walk levels
  const depth = tag.levels || 0;
  const parts = [];
  for (let i = 0; i < depth; i += 1) {
    const entry = tag[`level_${i}`];
    if (!entry) break;
    parts.push(isUkLocale() ? entry.name_uk || entry.name_en : entry.name_en || entry.name_uk);
  }
  return parts.join(separator);
};

export const getTagProof = (tag) => {
  if (!tag) return null;
  const link = tag.proof_link || null;
  const title = isUkLocale()
    ? tag.proof_title_uk || tag.proof_title_en || ''
    : tag.proof_title_en || tag.proof_title_uk || '';
  if (!link && !title) return null;
  return { link, title };
};

const getDepthClass = (depth) => {
  switch (depth) {
    case 0:
      return styles.depth0;
    case 1:
      return styles.depth1;
    case 2:
      return styles.depth2;
    case 3:
      return styles.depth3;
    default:
      return styles.depth4;
  }
};

/**
 * Shared, hierarchy-aware list of source tags with checkboxes.
 *
 * Props:
 *  - tags: array of tag objects from /API/v1/source_tags/public
 *  - selected: string[] (selected full_path values)
 *  - onChange: (newSelected: string[]) => void
 *  - showSelectAll: boolean (default true) renders select/deselect all control
 *  - idPrefix: string (default 'tag') used to namespace checkbox ids
 */
export const TagsTree = ({ tags, selected, onChange, showSelectAll = true, idPrefix = 'tag' }) => {
  const { t } = useTranslation();

  const sortedTags = useMemo(() => {
    if (!Array.isArray(tags)) return [];
    return [...tags].sort((a, b) => {
      const aPath = a?.full_path || '';
      const bPath = b?.full_path || '';
      return aPath.localeCompare(bPath);
    });
  }, [tags]);

  if (!sortedTags.length) {
    return null;
  }

  const allSelected =
    selected.length > 0 && sortedTags.every((tag) => selected.includes(tag.full_path));

  const handleToggleAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(sortedTags.map((tag) => tag.full_path));
    }
  };

  const handleToggleOne = (fullPath) => {
    if (selected.includes(fullPath)) {
      onChange(selected.filter((s) => s !== fullPath));
    } else {
      onChange([...selected, fullPath]);
    }
  };

  return (
    <div>
      {showSelectAll && (
        <div className={styles.control} onClick={handleToggleAll} role="button" tabIndex={0}>
          {allSelected ? t('Deselect all') : t('Select all')}
        </div>
      )}
      <ul className={styles.tagsTree}>
        {sortedTags.map((tag) => {
          const depth = Math.max(0, (tag.levels || 1) - 1);
          const leafName = getTagLeafName(tag);
          const fullPathLabel = getTagFullPathLabel(tag);
          const checkboxId = `${idPrefix}_${tag.full_path}`;
          return (
            <li key={tag.full_path} className={getDepthClass(depth)} title={fullPathLabel}>
              <input
                id={checkboxId}
                type="checkbox"
                checked={selected.includes(tag.full_path)}
                onChange={() => handleToggleOne(tag.full_path)}
              />
              <label htmlFor={checkboxId}>{leafName}</label>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
